import type { AgentMode, DeskDetail } from '@deskworks/shared-types';
import { MessageCircleQuestion, Zap } from 'lucide-react';
import type { ComponentProps } from 'react';
import { Confirm } from '@/components/confirm';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

export const AGENT_MODE_INFO: Record<AgentMode, { label: string; hint: string }> = {
  ask: {
    label: t('Off'),
    hint: t('Agents stop for you as the desk’s stage says. A question waits under “Needs you” until you answer; approvals wait for a person.'),
  },
  god: {
    label: t('God mode'),
    hint: t('Agents never stop for you and don’t give up: they decide themselves, try every way they have and list their assumptions in the result. Approval requests are granted automatically and logged.'),
  },
};

/** God mode on or off for a Desk's agents (docs/features/agent-mode-and-approvals.md); the stage is a switch of its own. */
export function AgentModeToggle({ desk }: { desk: DeskDetail }) {
  const save = useApiMutation((agentMode: AgentMode) => api.desks.update(desk.id, { agentMode }), {
    invalidate: [qk.desk(desk.id), qk.desks(desk.workspaceId)],
    success: (_, mode) => (mode === 'god' ? t('God mode on — agents work through without asking') : t('God mode off — agents stop as the stage says')),
  });
  const mode = desk.agentMode;

  return (
    <div className="inline-flex rounded-md border bg-muted/40 p-0.5 text-xs" role="radiogroup" aria-label={t('God mode')}>
      <Segment active={mode === 'ask'} title={AGENT_MODE_INFO.ask.hint} onClick={() => mode !== 'ask' && save.mutate('ask')}>
        <MessageCircleQuestion /> {t('Off')}
      </Segment>
      {mode === 'god' ? (
        <Segment active title={AGENT_MODE_INFO.god.hint} god>
          <Zap /> {t('God mode')}
        </Segment>
      ) : (
        <Confirm
          title={t('Switch this desk to God mode?')}
          description={
            <>
              {AGENT_MODE_INFO.god.hint}{' '}
              {t('What the desk guidelines leave to a person — approving, publishing, an expert check — the desk then does itself, unless the line says “also in God mode”. Approval rules, result approvals, tools that ask and work already waiting for you go to the desk’s Lead, which decides in your place. The Lead then goes through the whole desk and changes what would keep it from running on its own: guidelines, playbooks, start approval, tools that ask.')}
            </>
          }
          confirmLabel={t('Turn on God mode')}
          onConfirm={() => save.mutateAsync('god')}
        >
          <Segment active={false} title={AGENT_MODE_INFO.god.hint}>
            <Zap /> {t('God mode')}
          </Segment>
        </Confirm>
      )}
    </div>
  );
}

/** Forwards the trigger props Confirm (asChild) passes in. */
function Segment({ active, god, className, children, ...rest }: ComponentProps<'button'> & { active: boolean; god?: boolean }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      className={cn(
        'inline-flex h-6 cursor-pointer items-center gap-1 rounded px-2 font-medium text-muted-foreground transition-colors hover:text-foreground [&_svg]:size-3.5',
        active && 'bg-background text-foreground shadow-sm',
        active && god && 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
